import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/apiBaseUrl';
import { useAuth } from '../context/AuthContext';
import { fetchJson, isUnauthorizedError } from '../services/authFetch';
import { isViewer } from '../utils/roles';
import fertLogo from '../assets/fert-logo-black.png';
import '../styles/NavigationBar.css';

function formatTime(isoString) {
  if (!isoString) return null;

  try {
    return new Date(isoString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return null;
  }
}

function remainingClass(remaining, limit) {
  if (!limit) return '';

  const pct = remaining / limit;

  if (pct > 0.4) return 'api-usage-ok';
  if (pct > 0.1) return 'api-usage-warn';
  return 'api-usage-critical';
}

export function NavigationBar({ status, activeTab, onMenuToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [apiUsage, setApiUsage] = useState(null);
  const viewerMode = isViewer(user);

  useEffect(() => {
    if (viewerMode) {
      setApiUsage(null);
      return undefined;
    }

    const loadApiUsage = () => {
      fetchJson(`${API_BASE_URL}/market-watch/api-usage`)
        .then((data) => {
          if (data?.success) {
            setApiUsage(data.data);
          }
        })
        .catch((error) => {
          if (isUnauthorizedError(error)) {
            return;
          }

          console.error('Erreur chargement quota API:', error);
        });
    };

    loadApiUsage();
    const intervalId = window.setInterval(loadApiUsage, 30000);

    return () => window.clearInterval(intervalId);
  }, [viewerMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isEbaySynced = apiUsage?.data_source === 'ebay_headers';
  const resetTime = apiUsage?.ebay_reset_at
    ? formatTime(apiUsage.ebay_reset_at)
    : apiUsage?.reset_time ?? null;

  return (
    <header className="navbar">
      <div className="navbar-left">
        <button type="button" className="navbar-menu-button" onClick={onMenuToggle} aria-label="Ouvrir le menu">
          <span aria-hidden="true">≡</span>
        </button>

        <div className="navbar-brand">
          <div className="navbar-brand-mark">
            <img src={fertLogo} alt="Fert Recyclage" />
          </div>
          <div className="navbar-brand-copy">
            <span className="navbar-brand-kicker">Analyse marche PRE</span>
            <h2>{activeTab?.label || 'Dashboard'}</h2>
          </div>
        </div>
      </div>

      <div className="navbar-content">
        {apiUsage && (
          <div className="api-usage-banner">
            <span className="api-usage-label">eBay Browse API</span>

            <span className={`api-usage-value ${remainingClass(apiUsage.calls_remaining, apiUsage.daily_limit)}`}>
              {apiUsage.calls_remaining.toLocaleString('fr-FR')} restants
            </span>

            <span className="api-usage-detail">
              {apiUsage.calls_used.toLocaleString('fr-FR')} / {apiUsage.daily_limit.toLocaleString('fr-FR')}
            </span>

            {isEbaySynced ? (
              <span className="api-usage-source api-usage-source--ebay" title="Quota synchronise depuis les headers eBay">
                exact
              </span>
            ) : (
              <span className="api-usage-source api-usage-source--local" title="Comptage local lorsque les headers eBay sont absents">
                local
              </span>
            )}

            {resetTime && (
              <span className="api-usage-reset" title="Heure de reinitialisation du quota">
                reset {resetTime}
              </span>
            )}
          </div>
        )}

        <div className="navbar-status-badge">
          <span className={`navbar-status-dot ${status ? 'navbar-status-dot--online' : ''}`} />
          <span>{status || 'Backend indisponible'}</span>
        </div>

        <div className="navbar-user">
          <div className="user-avatar">
            {(user?.username || 'U').slice(0, 1).toUpperCase()}
          </div>

          <div className="navbar-user-copy">
            <span className="username">{user?.username}</span>
            <small>{user?.role || 'Utilisateur'}</small>
          </div>

          <div className="dropdown">
            <button
              className="dropdown-button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-label="Ouvrir les actions utilisateur"
            >
              <span aria-hidden="true">▾</span>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <button className="dropdown-item" onClick={handleLogout}>
                  Deconnexion
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
